import React, {useEffect, useState} from 'react';
import Title from 'src/components/AccountSettings/Title';
import Label from 'shared/components/label';

import AddressVerificationModal from 'src/components/address-verification-modal/address-verification-modal';
import {Formik} from 'formik';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import FormikAddressInput from 'src/components/inputs/formik-address-input/formik-address-input';
import GenderInput from 'src/components/inputs/gender-input';
import RaceOrEthnicity from 'src/components/race-ethnicity/race-ethnicity';
import ReligionInput from 'src/components/inputs/religion-input';
import InstitutionTypeaheadWithModal from 'shared/institution-typeahead/components/institution-typeahead-with-modal';
import {HighSchoolInfo} from 'src/types/education';
import CitizenshipInput from 'src/components/inputs/citizenship-input/citizenship-input';
import SexInput from 'src/components/inputs/sex-input/sex-input';
import FirstGenInput from 'src/components/inputs/first-gen-input/first-gen-input';
import LunchPlanInput from 'src/components/inputs/lunch-plan-input/lunch-plan-input';
import BirthdayInput from 'src/components/inputs/birthday-input/birthday-input';
import {
  profileFormSchemaCreator,
  ProfileFormSchemaFields,
} from 'src/components/AccountSettings/profile-form/schema';
import ProfileFormFooter from 'src/components/AccountSettings/profile-form/profile-form-footer/profile-form-footer';
import {
  Ethnicity,
  Gender,
  IsUsCitizenOrResident,
  Sex,
} from 'src/components/AccountSettings/profile-form/utils';
import {useDispatch} from 'react-redux';
import {errorMessage} from 'shared/actions/notifications';

interface Props {
  onSubmit: (fields: ProfileFormFields) => void;
  initialValues: ProfileFormFields;
}

interface ProfileFormFields {
  _id: string;
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  subdivision: string;
  country: string;
  postalCode: string;
  birthdayDay: number;
  birthdayMonth: number;
  birthdayYear: number;
  sex: Sex;
  gender: Gender;
  customGenderIdentity?: string;
  ethnicity: Ethnicity;
  religion: string;
  customReligion?: string;
  isUsCitizenOrResident?: IsUsCitizenOrResident;
  isFirstGen: boolean;
  currentInstitution?: string;
  highSchoolInfo: HighSchoolInfo;
}

const HIGH_SCHOOL_ACCOUNT_SETTINGS_VALIDATION_SCHEMA: ProfileFormSchemaFields = [
  'firstName',
  'lastName',
  'addressLine1',
  'addressLine2',
  'city',
  'subdivision',
  'country',
  'postalCode',
  'birthday',
  'gender',
  'ethnicity',
  'religion',
  'usCitizenOrResident',
  'lunchPlan',
  'isFirstGen',
  'currentInstitution',
  'sex',
];

const ProfileForm = ({onSubmit, initialValues}: Props) => {
  const [neverScroll, setNeverScroll] = useState(true);
  const dispatch = useDispatch();

  const scrollToQuestion = (question: string): void => {
    const questionId = document.getElementById(question + '-legend');
    if (questionId && neverScroll) {
      const parentOffset = questionId.parentElement?.offsetTop;
      if (parentOffset !== undefined) {
        setTimeout(() => {
          window.scrollTo(0, parentOffset);
        }, 2);
        setNeverScroll(false);
      }
    }
  };

  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      scrollToQuestion(hash);
    }
  }, []);

  return (
    <Formik<ProfileFormFields>
      initialValues={initialValues}
      validationSchema={profileFormSchemaCreator(
        HIGH_SCHOOL_ACCOUNT_SETTINGS_VALIDATION_SCHEMA,
        {}
      )}
      onSubmit={onSubmit}
    >
      {({validateForm}) => (
        <div className="account-settings-form">
          <AddressVerificationModal />
          <Title>Edit Profile</Title>
          <form autoComplete="off">
            <legend className="shared-required-legend"></legend>
            <section className="account-settings-form-section related">
              <div className="form-container">
                <div className="account-settings-form-section-side-by-side">
                  <Label required>
                    First Name
                    <FormikTextInput required name="firstName" />
                  </Label>
                  <Label required>
                    Last Name
                    <FormikTextInput required name="lastName" />
                  </Label>
                </div>
              </div>
            </section>
            <section className="account-settings-form-section related">
              <div className="form-container address-input">
                <InstitutionTypeaheadWithModal institutionType="high-school" />
              </div>
            </section>
            <section className="account-settings-form-section related">
              <div className="form-container address-input">
                <FormikAddressInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <BirthdayInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <GenderInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <SexInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <CitizenshipInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <LunchPlanInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <FirstGenInput />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <RaceOrEthnicity />
              </div>
            </section>
            <section className="account-settings-form-section">
              <div className="form-container">
                <ReligionInput />
              </div>
            </section>
          </form>
          <ProfileFormFooter<ProfileFormFields>
            onSubmitClick={async () => {
              const errors = await validateForm();
              if (Object.keys(errors).length > 0) {
                dispatch(errorMessage(`Form errors: ${Object.values(errors).join(', ')}`));
              }
            }}
          />
        </div>
      )}
    </Formik>
  );
};

export default ProfileForm;
